// AES-256-GCM helpers backed by the browser's Web Crypto API.
//
// Copied from advance-front (`src/app/shared/utils/crypto-aes-gcm.ts`) so the
// customer portal and ADVANCE use one scheme. The encrypt/decrypt pair is
// unchanged; `deriveScopedKeyB64` is added here because the portal is
// multi-tenant and ADVANCE is not.
//
// Wire format:
//
//   <base64> = base64( IV (12 bytes) || ciphertext || tag (16 bytes) )
//
// The key is supplied as a base64-encoded raw 32-byte AES-256 key; we import
// it once per process (cached by string) so encrypt / decrypt calls don't
// re-import the CryptoKey every time.
//
// NOTE: `crypto.subtle` only exists in a secure context — https, or localhost
// during development. On plain http it is undefined and every call here
// throws.

import { configuredEnv } from '../runtimeEnv';

const keyCache = new Map<string, Promise<CryptoKey>>();

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binary += String.fromCharCode.apply(
      null,
      Array.from(bytes.subarray(i, i + CHUNK)) as any,
    );
  }
  return btoa(binary);
}

function importKey(keyB64: string): Promise<CryptoKey> {
  const cached = keyCache.get(keyB64);
  if (cached) return cached;
  const raw = base64ToBytes(keyB64);
  // Promise.resolve() because this TS lib types subtle.importKey as
  // PromiseLike, not Promise.
  const promise = Promise.resolve(crypto.subtle.importKey(
    'raw',
    raw as any,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  ));
  keyCache.set(keyB64, promise);
  return promise;
}

// Per-tenant key material: SHA-256 over `<base key>:<hostname>` gives the
// 32 raw bytes AES-256 requires (the base key and hostname cannot simply be
// concatenated — importKey demands exactly 32 bytes).
//
// This is NOT tenant security. Both inputs are public — REACT_APP_* values are
// compiled into the JS bundle, and the hostname is visible to everyone — so
// anyone can derive any tenant's key. What it buys is that a blob encrypted on
// one client's domain is not decryptable with another's key, which keeps
// tenants from accidentally reading each other's stored values. Real tenant
// isolation is enforced by the API on every request.
export async function deriveScopedKeyB64(): Promise<string> {
  const baseKey = configuredEnv(process.env.REACT_APP_ENCRYPTION_KEY);
  if (!baseKey) {
    // Fail loudly rather than silently hashing "undefined:<host>" into a
    // predictable key. A missing env var then breaks login in staging, which
    // is where we want to find out.
    throw new Error(
      'REACT_APP_ENCRYPTION_KEY is not set. Docker deployments set it on the ' +
      'container (inject-runtime-env.sh fills it in at start); local builds ' +
      'read it from .env when `react-scripts build` runs.',
    );
  }
  const material = `${baseKey}:${window.location.hostname}`;
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(material) as any,
  );
  return bytesToBase64(new Uint8Array(digest));
}

export async function encryptPayload(
  data: unknown,
  keyB64: string,
): Promise<string> {
  const key = await importKey(keyB64);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(JSON.stringify(data));
  const cipherBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as any },
    key,
    encoded as any,
  );
  const cipher = new Uint8Array(cipherBuf);
  const combined = new Uint8Array(iv.byteLength + cipher.byteLength);
  combined.set(iv, 0);
  combined.set(cipher, iv.byteLength);
  return bytesToBase64(combined);
}

// Decrypts the base64 envelope. Returns the parsed JSON value; throws if the
// payload is malformed or tampered with (AES-GCM authenticates the ciphertext).
//
// Layout matches the encrypt side: `IV(12) || ciphertext || tag(16)`.
// Web Crypto's `decrypt` expects ciphertext+tag concatenated, which is exactly
// `combined.slice(12)`, so we don't separate them.
export async function decryptResponse<T = unknown>(
  b64: string,
  keyB64: string,
): Promise<T> {
  const key = await importKey(keyB64);
  // PHP's json_encode escapes `/` as `\/`. When the backend wraps the base64
  // in a JSON envelope that escape can leak into the string we receive, and
  // atob() rejects backslashes. Unescape defensively — a no-op when the
  // slashes were already raw.
  const cleaned = b64.replace(/\\\//g, '/');
  const combined = base64ToBytes(cleaned);
  const iv = combined.slice(0, 12);
  const cipherWithTag = combined.slice(12);
  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as any },
    key,
    cipherWithTag as any,
  );
  const json = new TextDecoder().decode(plainBuf);
  return JSON.parse(json) as T;
}
